<template>
  <Modal :open="open" @close="close">
    <div>
      <!-- Header -->
      <h1 class="text-center font-extrabold text-xl">Modifier les informations du contrat</h1>

      <!-- Plan Sale Information Section -->
      <div>
        <p class="font-bold text-xl pb-2 tracking-tight text-gray-900">
          {{ $t("Plan Sale") }}
        </p>
        <div class="flex-1 mt-1">
          <FormField id="planSaleDate" label="Date de début du contrat" type="date" v-model="localPlanSale.date" />
        </div>
        <div class="flex-1 mt-1">
          <FormField
            id="planSaleExpirationDate"
            label="Date d'expiration du contrat"
            type="date"
            v-model="localPlanSale.expiration_date"
          />
        </div>
      </div>

      <!-- Customer Information Section -->
      <div class="mt-4">
        <p class="font-bold text-xl pb-2 tracking-tight text-gray-900">
          {{ $t("Customer") }}
        </p>
        <div class="flex-1 mt-1">
          <FormField id="planSaleCustomerEmail" label="Email" type="email" v-model="localPlanSale.customer.email" />
        </div>
        <div class="flex-1 mt-1">
          <FormField
            id="planSaleCustomerInformationLastName"
            label="Nom"
            type="text"
            v-model="localPlanSale.customer_information.last_name"
          />
        </div>
        <div class="flex-1 mt-2">
          <FormField
            id="planSaleCustomerInformationFirstName"
            label="Prénom"
            type="text"
            v-model="localPlanSale.customer_information.first_name"
          />
        </div>
        <div class="flex-1 mt-2">
          <FormField
            id="planSaleCustomerInformationPhone"
            label="Numéro de téléphone"
            type="tel"
            v-model="localPlanSale.customer_information.phone"
          />
        </div>
        <div class="flex-1 mt-2">
          <FormField
            id="planSaleCustomerInformationBirthDate"
            :label="t('birth_date_label')"
            type="date"
            v-model="localPlanSale.customer_information.birth_date"
          />
        </div>
        <div class="flex-1 mt-2">
          <GoogleAddress class="flex-1" :initialAddress="customerAddress" @newAddress="updateCustomerAddress" />
        </div>
      </div>

      <!-- Products Information Section -->
      <div v-if="localPlanSale.plan_sale_product" class="mt-4">
        <p class="font-bold text-xl pb-2 tracking-tight text-gray-900">
          {{ $t("Products") }}
        </p>
        <Alert v-if="hasActiveClaims" type="warning" class="mb-4">
          <template #title>{{ t("active_claims_warning_title") }}</template>
          <span class="block mt-1 text-xs">{{ t("active_claims_warning_description") }}</span>
        </Alert>
        <div v-for="product in localPlanSale.plan_sale_product" :key="product.id" class="mt-2">
          <div class="flex-1 mt-1">
            <FormField :id="'planSaleProductTitle-' + product.id" label="Title" type="text" v-model="product.title" />
          </div>
          <div class="flex-1 mt-2">
            <FormField
              :id="'planSaleProductReferenceId-' + product.id"
              label="Reference ID"
              type="text"
              v-model="product.reference_id"
            />
          </div>
          <div v-if="!isVirtualProduct(product)" class="flex-1 mt-2">
            <FormField :id="'planSaleProductPrice' + product.id" label="Prix" type="number" v-model="product.price" />
          </div>
          <div class="flex-1 mt-2">
            <FormField
              :id="'planSaleProductPurchaseDate-' + product.id"
              :label="t('purchase_date_label')"
              type="date"
              v-model="product.purchase_date"
              :error="isFutureDate(product.purchase_date) ? t('purchase_date_in_future') : null"
            />
          </div>
        </div>
      </div>

      <!-- Footer Section -->
      <div class="mt-6 flex flex-col items-center space-y-4 text-center">
        <p class="text-sm text-gray-700 max-w-md">
          Si vous modifiez une information qui figure sur le bulletin d'adhésion, un nouveau bulletin est généré et
          envoyé au client assuré.
        </p>
        <GenericButton :disabled="hasFuturePurchaseDate" @click="updatePlanSale">Modifier le contrat</GenericButton>
      </div>
    </div>
  </Modal>
</template>

<script>
// UI Components
import { Modal, GenericButton, GoogleAddress, FormField, Alert, isVirtualProduct } from "@estaly/ui";
import { useI18n } from "vue-i18n";
import { mapGetters } from "vuex";

export default {
  name: "UpdateModal",

  components: {
    Modal,
    GenericButton,
    GoogleAddress,
    FormField,
    Alert,
  },

  setup() {
    const { t } = useI18n();
    return { t };
  },

  props: {
    open: {
      type: Boolean,
      required: true,
    },
    planSale: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      localPlanSale: {},
    };
  },

  computed: {
    ...mapGetters("claims", ["isActiveClaim"]),

    hasActiveClaims() {
      return (this.planSale.claims || []).some((claim) => this.isActiveClaim(claim));
    },

    hasFuturePurchaseDate() {
      return (this.localPlanSale.plan_sale_product || []).some((product) => this.isFutureDate(product.purchase_date));
    },

    // Address formatting for Google Address component
    customerAddress() {
      return {
        address: `${this.planSale.customer.address?.address1}, ${this.planSale.customer.address?.city}, ${this.planSale.customer.address?.province}, ${this.planSale.customer.address?.country}`,
        city: this.planSale.customer.address?.city,
        province: this.planSale.customer.address?.province,
        country: this.planSale.customer.address?.country,
        zipCode: this.planSale.customer.address?.zipCode,
      };
    },
  },

  mounted() {
    // Initialize local plan sale data from props
    this.localPlanSale = {
      status: this.planSale.status,
      date: this.convertDate(this.planSale.date),
      expiration_date: this.convertDate(this.planSale.expirationDate),
      customer: {
        email: this.planSale.customer.email,
      },
      customer_information: {
        first_name: this.planSale.customer.firstName,
        last_name: this.planSale.customer.lastName,
        phone: this.planSale.customer.phone,
        birth_date: this.convertDate(this.planSale.customer.birthDate),
        address1: this.planSale.customer.address?.address1,
        city: this.planSale.customer.address?.city,
        province: this.planSale.customer.address?.province,
        country: this.planSale.customer.address?.country,
        zip_code: this.planSale.customer.address?.zipCode,
      },
      plan_sale_product: this.planSale.products?.map((product) => ({
        id: product.id,
        title: product.title,
        reference_id: product.referenceId,
        price: product.price,
        purchase_date: this.convertDate(product.purchaseDate),
        product_nature: product.productNature,
      })),
    };
  },

  methods: {
    isVirtualProduct,

    // Event handlers
    updatePlanSale() {
      if (this.hasFuturePurchaseDate) return;

      this.$emit("updatePlanSale", this.localPlanSale);
    },

    close() {
      this.$emit("closeUpdateModal");
    },

    // Data handling methods
    updateCustomerAddress(newAddress) {
      this.localPlanSale.customer_information = {
        ...this.localPlanSale.customer_information,
        address1: newAddress.street,
        city: newAddress.city,
        province: newAddress.province,
        country: newAddress.country,
        zip_code: newAddress.zipCode,
      };
    },

    // Utility methods
    isFutureDate(dateStr) {
      // Dates are "yyyy-MM-dd" strings, so they compare lexicographically
      const now = new Date();
      const today = [now.getFullYear(), now.getMonth() + 1, now.getDate()]
        .map((part) => String(part).padStart(2, "0"))
        .join("-");
      return Boolean(dateStr) && dateStr > today;
    },

    convertDate(dateStr) {
      // Convert date from format "dd/mm/yyyy" to format "yyyy-MM-dd"
      if (dateStr) {
        const parts = dateStr.split("/");
        if (parts.length === 3) {
          return `${parts[2]}-${parts[1]}-${parts[0]}`;
        }
      }
      return null;
    },
  },
};
</script>

<i18n src="./UpdateModal.i18n.json"></i18n>
